import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const BreakevenCalculator = () => {
  const [fixedCosts, setFixedCosts] = useState(100000);
  const [variableCostPerUnit, setVariableCostPerUnit] = useState(200);
  const [sellingPricePerUnit, setSellingPricePerUnit] = useState(500);
  const [targetProfit, setTargetProfit] = useState(50000);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const fixed = Number(fixedCosts) || 0;
    const variable = Number(variableCostPerUnit) || 0;
    const selling = Number(sellingPricePerUnit) || 0;
    const target = Number(targetProfit) || 0;
    
    const contributionMargin = selling - variable;
    const contributionMarginRatio = selling > 0 ? (contributionMargin / selling) * 100 : 0;
    
    // Breakeven point
    const breakevenUnits = contributionMargin > 0 ? fixed / contributionMargin : 0;
    const breakevenRevenue = breakevenUnits * selling;
    
    // Units for target profit
    const unitsForTargetProfit = contributionMargin > 0 ? (fixed + target) / contributionMargin : 0;
    const revenueForTargetProfit = unitsForTargetProfit * selling;
    
    // Margin of safety (assuming current sales = target profit sales)
    const currentSales = revenueForTargetProfit;
    const marginOfSafety = currentSales - breakevenRevenue;
    const marginOfSafetyPercent = currentSales > 0 ? (marginOfSafety / currentSales) * 100 : 0;
    
    // Generate chart data
    const maxUnits = Math.ceil(unitsForTargetProfit * 1.5) || 100;
    const chartLabels = [];
    const revenueData = [];
    const totalCostData = [];
    const profitData = [];
    
    for (let units = 0; units <= maxUnits; units += Math.ceil(maxUnits / 10)) {
      chartLabels.push(units);
      revenueData.push(units * selling);
      totalCostData.push(fixed + (units * variable));
      profitData.push((units * selling) - fixed - (units * variable));
    }

    setResult({
      contributionMargin,
      contributionMarginRatio,
      breakevenUnits: Math.ceil(breakevenUnits),
      breakevenRevenue,
      unitsForTargetProfit: Math.ceil(unitsForTargetProfit),
      revenueForTargetProfit,
      marginOfSafety,
      marginOfSafetyPercent,
      chartLabels,
      revenueData,
      totalCostData,
      profitData,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Break-even Analysis', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['Fixed Costs', formatCurrencyForPDF(fixedCosts)],
        ['Variable Cost per Unit', formatCurrencyForPDF(variableCostPerUnit)],
        ['Selling Price per Unit', formatCurrencyForPDF(sellingPricePerUnit)],
        ['Contribution Margin', formatCurrencyForPDF(result.contributionMargin)],
        ['Contribution Margin Ratio', `${result.contributionMarginRatio.toFixed(2)}%`],
        ['Break-even Units', result.breakevenUnits.toLocaleString()],
        ['Break-even Revenue', formatCurrencyForPDF(result.breakevenRevenue)],
        ['Target Profit', formatCurrencyForPDF(targetProfit)],
        ['Units for Target Profit', result.unitsForTargetProfit.toLocaleString()],
        ['Revenue for Target Profit', formatCurrencyForPDF(result.revenueForTargetProfit)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Breakeven_Analysis.pdf');
  };

  const chartData = result ? {
    labels: result.chartLabels,
    datasets: [
      {
        label: 'Revenue',
        data: result.revenueData,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Total Cost',
        data: result.totalCostData,
        borderColor: '#ef4444',
        tension: 0.1,
      },
      {
        label: 'Profit/Loss',
        data: result.profitData,
        borderColor: '#3b82f6',
        borderDash: [5, 5],
        tension: 0.1,
      },
    ]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Breakeven Calculator"
        description="Free Breakeven Calculator - Calculate breakeven point for business. Units and revenue needed."
        keywords="breakeven calculator, break even point calculator, breakeven analysis, BEP calculator, business breakeven calculator"
        canonicalPath="/breakeven"
      />
      <div className="calculator-header">
        <h1>⚖️ Break-even Calculator</h1>
        <p>Calculate when your business becomes profitable</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Fixed Costs (₹)</label>
            <input
              type="number"
              value={fixedCosts === '' ? '' : fixedCosts}
              onChange={(e) => setFixedCosts(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>Rent, salaries, insurance, etc. (regardless of sales)</small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Variable Cost per Unit (₹)</label>
              <input
                type="number"
                value={variableCostPerUnit === '' ? '' : variableCostPerUnit}
                onChange={(e) => setVariableCostPerUnit(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Selling Price per Unit (₹)</label>
              <input
                type="number"
                value={sellingPricePerUnit === '' ? '' : sellingPricePerUnit}
                onChange={(e) => setSellingPricePerUnit(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Target Profit (₹)</label>
            <input
              type="number"
              value={targetProfit === '' ? '' : targetProfit}
              onChange={(e) => setTargetProfit(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Break-even
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                <h3>Break-even Point</h3>
                <div className="amount">{result.breakevenUnits.toLocaleString()} Units</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Revenue: {formatCurrency(result.breakevenRevenue)}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item" style={{ background: '#dbeafe' }}>
                  <span className="label">Contribution Margin</span>
                  <span className="value">{formatCurrency(result.contributionMargin)}/unit</span>
                </div>
                <div className="result-item">
                  <span className="label">Contribution Margin Ratio</span>
                  <span className="value">{result.contributionMarginRatio.toFixed(2)}%</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Break-even Units</span>
                  <span className="value" style={{ color: '#f59e0b', fontWeight: '600' }}>{result.breakevenUnits.toLocaleString()}</span>
                </div>
                <div className="result-item">
                  <span className="label">Break-even Revenue</span>
                  <span className="value">{formatCurrency(result.breakevenRevenue)}</span>
                </div>
                
                <div style={{ marginTop: '16px', padding: '12px', background: '#ecfdf5', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '12px', color: '#059669' }}>For Target Profit of {formatCurrency(targetProfit)}</h4>
                  <div className="result-item">
                    <span className="label">Units Required</span>
                    <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{result.unitsForTargetProfit.toLocaleString()}</span>
                  </div>
                  <div className="result-item">
                    <span className="label">Revenue Required</span>
                    <span className="value">{formatCurrency(result.revenueForTargetProfit)}</span>
                  </div>
                </div>

                <div className="result-item" style={{ marginTop: '16px' }}>
                  <span className="label">Margin of Safety</span>
                  <span className="value">{formatCurrency(result.marginOfSafety)} ({result.marginOfSafetyPercent.toFixed(1)}%)</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Break-even Chart</h4>
                  <Line data={chartData} options={{ 
                    responsive: true, 
                    plugins: { 
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.dataset.label}: ₹${context.raw.toLocaleString('en-IN')}`
                        }
                      }
                    },
                    scales: {
                      y: {
                        ticks: {
                          callback: (value) => '₹' + value.toLocaleString('en-IN')
                        }
                      }
                    }
                  }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download Analysis PDF
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your cost and pricing details to calculate break-even</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Break-even Concepts</h4>
        <ul>
          <li><strong>Fixed Costs:</strong> Costs that don't change with sales volume</li>
          <li><strong>Variable Costs:</strong> Costs that change with each unit sold</li>
          <li><strong>Contribution Margin:</strong> Selling Price - Variable Cost</li>
          <li><strong>Break-even:</strong> Fixed Costs ÷ Contribution Margin</li>
          <li><strong>Margin of Safety:</strong> How much sales can drop before loss</li>
        </ul>
      </div>
    </div>
  );
};

export default BreakevenCalculator;
